import { inject, Injectable, Injector, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import {
    GoogleAnalyticsService,
    LocalStorageService,
} from '@interacta-shared/data-access-common';
import { ModalSurfaceService } from '@interacta-shared/ui';
import { AIDiscoveryCoachMarkCloseReason } from '@interacta-shared/ui-ai';
import { CarouselService } from '@modules/carousel/services/carousel.service';
import { isSameDay } from 'date-fns';
import {
    combineLatest,
    EMPTY,
    filter,
    fromEvent,
    map,
    merge,
    Observable,
    switchMap,
    take,
} from 'rxjs';

const STORAGE_KEY = 'discovery:ai-conversational';
const MAX_SHOWN_COUNT = 3;

type AIDiscoveryOutcome = 'pending' | 'watched' | 'dismissed';

export type AIDiscoveryOpenSource = 'coach_mark' | 'ai_panel_settings';

interface AIDiscoveryState {
    shownCount: number;
    lastShownAt: number | null;
    outcome: AIDiscoveryOutcome;
}

const INITIAL_STATE: AIDiscoveryState = {
    shownCount: 0,
    lastShownAt: null,
    outcome: 'pending',
};

@Injectable({ providedIn: 'root' })
export class AIDiscoveryService {
    private readonly localStorageService = inject(LocalStorageService);
    private readonly gaService = inject(GoogleAnalyticsService);
    private readonly carouselService = inject(CarouselService);
    private readonly modalSurfaceService = inject(ModalSurfaceService);
    private readonly injector = inject(Injector);

    private state: AIDiscoveryState =
        this.localStorageService.getEntry<AIDiscoveryState>(
            STORAGE_KEY,
            'ifLogged',
        ) ?? INITIAL_STATE;

    readonly isCoachMarkOpen = signal(false);
    readonly isDialogOpen = signal(false);

    /**
     * Emits once when the coach-mark may be shown: the first time the window
     * comes back to the foreground, and never on the visit that opened the app.
     */
    nextOccasion(): Observable<void> {
        if (!this.canShow()) {
            return EMPTY;
        }

        return this.backToForeground().pipe(
            switchMap(() => this.screenIsFree()),
            take(1),
        );
    }

    openCoachMark(): void {
        this.patchState({
            shownCount: this.state.shownCount + 1,
            lastShownAt: Date.now(),
        });
        this.isCoachMarkOpen.set(true);
    }

    /**
     * Escape is as deliberate a refusal as the close button; a detach is the
     * page navigating away, so only the first two are counted.
     */
    closeCoachMark(reason: AIDiscoveryCoachMarkCloseReason = 'button'): void {
        // The CDK detaches the pane on every close, programmatic ones included,
        // so this runs a second time after openDialog() has already closed the
        // coach-mark — and would overwrite the outcome it just wrote.
        if (!this.isCoachMarkOpen()) {
            return;
        }
        this.isCoachMarkOpen.set(false);
        if (reason !== 'detach') {
            this.gaService.sendCustomEvent(
                'isp_ai_conversational_discovery_skipped',
                { shown_count: this.state.shownCount },
            );
        }
        if (this.state.shownCount >= MAX_SHOWN_COUNT) {
            this.patchState({ outcome: 'dismissed' });
        }
    }

    /**
     * Opening the AI panel is more than the coach-mark was asking for, so it
     * counts as a success rather than a dismissal — but only while the
     * coach-mark is the thing that prompted it.
     */
    aiPanelOpened(): void {
        if (!this.isCoachMarkOpen()) {
            return;
        }
        this.isCoachMarkOpen.set(false);
        this.patchState({ outcome: 'watched' });
    }

    openDialog(source: AIDiscoveryOpenSource): void {
        this.isCoachMarkOpen.set(false);
        this.isDialogOpen.set(true);
        this.patchState({ outcome: 'watched' });
        this.gaService.sendCustomEvent(
            source === 'coach_mark'
                ? 'isp_ai_conversational_discovery_opened'
                : 'isp_ai_conversational_menu_video_opened',
        );
    }

    closeDialog(): void {
        this.isDialogOpen.set(false);
    }

    private canShow(): boolean {
        return (
            this.state.outcome === 'pending' &&
            this.state.shownCount < MAX_SHOWN_COUNT &&
            !this.wasShownToday()
        );
    }

    /**
     * Neither event fires for the visit that opened the app: `visibilitychange`
     * only reports a change, and a window focused since load never emits
     * `focus`. Both are listened to because they cover different gestures —
     * switching tab raises the first, switching application the second.
     */
    private backToForeground(): Observable<void> {
        return merge(
            fromEvent(document, 'visibilitychange').pipe(
                filter(() => document.visibilityState === 'visible'),
            ),
            fromEvent(window, 'focus'),
        ).pipe(map(() => undefined));
    }

    /**
     * A surface that owns the screen — a lightbox, a dialog, the intro
     * carousel, a blocking system notice — leaves the coach-mark painting over
     * it while staying unreachable by keyboard: it is a CDK pane, so nothing
     * stacks it under the focus trap in front of it. All of them carry the
     * shared marker once mounted; a carousel still being fetched is the one
     * case with nothing in the document yet, so it is waited on through its
     * own signal.
     */
    private screenIsFree(): Observable<void> {
        return combineLatest([
            toObservable(this.carouselService.isCarouselPending, {
                injector: this.injector,
            }),
            this.modalSurfaceService.isAnyModalOpen$(),
        ]).pipe(
            filter(
                ([isCarouselPending, isModalOpen]) =>
                    !isCarouselPending && !isModalOpen,
            ),
            take(1),
            map(() => undefined),
        );
    }

    private wasShownToday(): boolean {
        const lastShownAt = this.state.lastShownAt;
        return lastShownAt != null && isSameDay(lastShownAt, Date.now());
    }

    private patchState(patch: Partial<AIDiscoveryState>): void {
        this.state = { ...this.state, ...patch };
        this.localStorageService.setEntry(STORAGE_KEY, this.state, 'ifLogged');
    }
}
